import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Book, 
  RefreshCw, 
  Database, 
  Image, 
  Play, 
  Pause, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { fixAllForeignCovers } from '@/utils/fixForeignCovers';
import { UserLibraryEnrichmentPanel } from '@/components/admin/UserLibraryEnrichmentPanel';
import { OpenLibraryResetPanel } from '@/components/admin/OpenLibraryResetPanel';
import { EnrichmentMonitoringDashboard } from '@/components/admin/EnrichmentMonitoringDashboard';
import { EnrichmentQueueTable } from '@/components/admin/EnrichmentQueueTable';
import { CoverCacheManager } from '@/components/admin/CoverCacheManager';
import { testIsbndbForSeries } from '@/utils/testIsbndbSeries';
import { runCompleteSeriesBackfill, cleanupJunkSeriesData, getSeriesStats, backfillSeriesFromTitles } from '@/utils/backfillSeriesData';

interface EnrichmentJob {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  job_type: string;
  total_books: number;
  processed_books: number;
  failed_books: number;
  error_message?: string;
  config: any;
  results: any;
}

interface BookStats {
  total: number;
  pending: number;
  completed: number;
  failed: number;
  duplicates: number;
  with_covers: number;
  cached_covers: number;
}

interface QualityIssue {
  type: string;
  severity: 'high' | 'medium' | 'low';
  book_id: string;
  title: string;
  authors: string[];
  user_count: number;
  message: string;
  confidence?: number;
  page_count?: number;
  language?: string;
}

export default function AdminEnrichment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState<EnrichmentJob[]>([]);
  const [stats, setStats] = useState<BookStats | null>(null);
  const [currentJob, setCurrentJob] = useState<EnrichmentJob | null>(null);
  const [fixProgress, setFixProgress] = useState<string>('');
  const [qualityIssues, setQualityIssues] = useState<QualityIssue[]>([]);
  const [qualitySummary, setQualitySummary] = useState<{ high: number; medium: number; low: number } | null>(null);
  const [showQualityCheck, setShowQualityCheck] = useState(false);
  // Check if user is admin using proper role-based access control
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }
      
      // Check if user has admin role using security definer function
      const { data: hasAdminRole, error } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'admin' });
      
      if (error || !hasAdminRole) {
        console.error('Admin access denied:', error);
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have permission to access this page.",
        });
        navigate('/');
        return;
      }
      
      loadData();
    };

    checkAdminAccess();
  }, [user, navigate, toast]);

  const loadData = async () => {
    try {
      await Promise.all([
        loadJobs(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load enrichment data.",
      });
    }
  };

  const loadJobs = async () => {
    const { data, error } = await supabase
      .from('enrichment_jobs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      throw error;
    }

    setJobs(data || []);
    
    // Set current job if there's a running one
    const runningJob = data?.find(job => job.status === 'running');
    if (runningJob) {
      setCurrentJob(runningJob);
    }
  };

  const loadStats = async () => {
    // Get book statistics
    const [
      totalResult,
      pendingResult,
      completedResult,
      failedResult,
      duplicatesResult,
      coversResult,
      cachedCoversResult
    ] = await Promise.all([
      supabase.from('books').select('*', { count: 'exact', head: true }),
      supabase.from('books').select('*', { count: 'exact', head: true }).eq('enrichment_status', 'pending'),
      supabase.from('books').select('*', { count: 'exact', head: true }).eq('enrichment_status', 'completed'),
      supabase.from('books').select('*', { count: 'exact', head: true }).eq('enrichment_status', 'failed'),
      supabase.from('books').select('*', { count: 'exact', head: true }).not('duplicate_of', 'is', null),
      supabase.from('books').select('*', { count: 'exact', head: true }).not('cover_url', 'is', null),
      supabase.from('books').select('*', { count: 'exact', head: true }).eq('cover_cached', true)
    ]);

    setStats({
      total: totalResult.count || 0,
      pending: pendingResult.count || 0,
      completed: completedResult.count || 0,
      failed: failedResult.count || 0,
      duplicates: duplicatesResult.count || 0,
      with_covers: coversResult.count || 0,
      cached_covers: cachedCoversResult.count || 0,
    });
  };

  const startEnrichmentJob = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-books', {
        body: { action: 'start-enrichment-job' }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Enrichment job started! Processing ${data.totalBooks} books.`,
      });

      // Reload data to show the new job
      await loadData();
      
      // Start polling for job status
      pollJobStatus();
      
    } catch (error: any) {
      console.error('Error starting enrichment job:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to start enrichment job.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const runDeduplication = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-books', {
        body: { action: 'deduplicate' }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Deduplication complete! Merged ${data.merged} duplicate pairs.`,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error running deduplication:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to run deduplication.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cacheCovers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-books', {
        body: { action: 'cache-covers', batchSize: 20 }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Cover caching complete! Cached ${data.cached} covers.`,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error caching covers:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to cache covers.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const populatePopularBooks = async () => {
    const confirmed = window.confirm(
      'This will import 5,000 popular books from OpenLibrary.\n\n' +
      'This action will:\n' +
      '• Add high-quality, frequently searched books\n' +
      '• Trigger enrichment for full metadata\n' +
      '• Cache covers for instant loading\n\n' +
      'This process may take 15-20 minutes. Continue?'
    );
    
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('populate-recommended-books', {
        body: { 
          config: { 
            source: 'popular',
            limit: 5000
          }
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Imported ${data.totalImported} popular books! ${data.totalEnriched} enriched.`,
        duration: 10000,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error populating books:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to populate books.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const runWeeklyImport = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('weekly-import-job');

      if (error) throw error;

      toast({
        title: "Success",
        description: `Weekly import complete! Imported: ${data.imported}, Updated: ${data.updated}`,
        duration: 10000,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error running weekly import:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to run weekly import.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cleanupEnrichmentQueue = async () => {
    const confirmed = window.confirm(
      'This will clean up the enrichment queue by:\n\n' +
      '• Marking books that already have all data as "complete"\n' +
      '• Marking stale pending books (>30 days) as "no_data_found"\n\n' +
      'This will significantly reduce the pending queue. Continue?'
    );
    
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-enrichment-queue');

      if (error) throw error;

      toast({
        title: "Cleanup Complete!",
        description: `Removed ${data.completedCount + data.staleCount} books from queue. ${data.remainingPending} still pending.`,
        duration: 10000,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error cleaning up queue:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to cleanup enrichment queue.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fixAllForeignCoversHandler = async () => {
    const confirmed = window.confirm(
      'This will find and re-enrich ALL books with foreign or unknown language covers.\n\n' +
      'This action will:\n' +
      '• Clear existing covers for affected books\n' +
      '• Trigger English-only cover enrichment\n' +
      '• Process books in batches of 50\n\n' +
      'This process may take several minutes. Continue?'
    );
    
    if (!confirmed) return;
    
    setIsLoading(true);
    setFixProgress('Starting...');
    
    try {
      setFixProgress('Finding books with foreign covers...');
      
      const result = await fixAllForeignCovers(50);

      if (result.totalFound === 0) {
        toast({
          title: "Info",
          description: "No books with foreign covers found.",
        });
        setFixProgress('');
        return;
      }

      toast({
        title: "Success",
        description: `Fixed ${result.totalFound} books! Cleared ${result.cleared} covers, ${result.enriched} successfully re-enriched across ${result.batches} batches.`,
        duration: 10000,
      });

      await loadData();
      setFixProgress('');
      
    } catch (error: any) {
      console.error('Error fixing foreign covers:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fix foreign covers.",
      });
      setFixProgress('');
    } finally {
      setIsLoading(false);
    }
  };

  const runQualityCheck = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-books', {
        body: { action: 'quality-check' }
      });

      if (error) {
        throw error;
      }

      setQualityIssues(data.issues || []);
      setQualitySummary(data.summary || { high: 0, medium: 0, low: 0 });
      setShowQualityCheck(true);

      toast({
        title: "Quality Check Complete",
        description: `Found ${data.total_issues} issues: ${data.summary.high} high, ${data.summary.medium} medium, ${data.summary.low} low`,
      });

    } catch (error: any) {
      console.error('Error running quality check:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to run quality check.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testIsbndbSeries = async () => {
    setIsLoading(true);
    try {
      // Test with ACOTAR ISBN
      const isbn = '9781619635180';
      
      toast({
        title: "Testing ISBNdb",
        description: `Checking series data for ACOTAR (${isbn})...`,
      });
      
      const result = await testIsbndbForSeries(isbn);
      
      console.log('[Admin] ISBNdb test result:', result);
      
      if (result) {
        toast({
          title: "ISBNdb Response",
          description: `Series: ${result.series || 'None'}, Volume: ${result.volume || 'None'}. Check console for full details.`,
          duration: 10000,
        });
      }
    } catch (error: any) {
      console.error('Error testing ISBNdb:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to test ISBNdb.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cleanupJunkBooks = async () => {
    const confirmed = window.confirm(
      'This will DELETE low-quality OpenLibrary books that:\n\n' +
      '• Have no cover image\n' +
      '• Have no page count or less than 50 pages\n' +
      '• Are NOT in any user\'s library\n\n' +
      'This may delete thousands of books. Continue?'
    );
    
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      // Run in batches of 5000
      let totalDeleted = 0;
      let hasMore = true;
      
      while (hasMore) {
        const { data, error } = await supabase.functions.invoke('cleanup-junk-books', {
          body: { action: 'aggressive-openlibrary', dryRun: false, limit: 5000 }
        });

        if (error) throw error;
        
        totalDeleted += data.stats?.books_deleted || 0;
        
        toast({
          title: "Cleanup Progress",
          description: `Deleted ${totalDeleted} junk books so far...`,
        });
        
        // Stop if no more books deleted or scanned less than limit
        if (!data.stats?.books_deleted || data.stats.books_deleted === 0 || data.stats.total_scanned < 5000) {
          hasMore = false;
        }
      }

      toast({
        title: "Cleanup Complete!",
        description: `Deleted ${totalDeleted} low-quality OpenLibrary books.`,
        duration: 10000,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error cleaning up junk books:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to cleanup junk books.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const purgeNoCoverBooks = async () => {
    const confirmed = window.confirm(
      '⚠️ AGGRESSIVE PURGE ⚠️\n\n' +
      'This will DELETE ALL books (from any source) that:\n\n' +
      '• Have NO cover image\n' +
      '• Are NOT in any user\'s library or saves\n\n' +
      'This includes ai_recommendation, openlibrary, isbndb, etc.\n' +
      'This may delete ~3,500+ books. Continue?'
    );
    
    if (!confirmed) return;
    
    setIsLoading(true);
    try {
      let totalDeleted = 0;
      let hasMore = true;
      
      while (hasMore) {
        const { data, error } = await supabase.functions.invoke('cleanup-junk-books', {
          body: { action: 'purge-no-cover', dryRun: false, limit: 5000 }
        });

        if (error) throw error;
        
        totalDeleted += data.stats?.books_deleted || 0;
        
        toast({
          title: "Purge Progress",
          description: `Deleted ${totalDeleted} books without covers so far...`,
        });
        
        // Stop if no more books deleted or scanned less than limit
        if (!data.stats?.books_deleted || data.stats.books_deleted === 0 || data.stats.total_scanned < 5000) {
          hasMore = false;
        }
      }

      toast({
        title: "Purge Complete!",
        description: `Deleted ${totalDeleted} books without covers.`,
        duration: 10000,
      });

      await loadData();
      
    } catch (error: any) {
      console.error('Error purging no-cover books:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to purge books.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const pollJobStatus = async () => {
    // Poll every 5 seconds for job updates
    const interval = setInterval(async () => {
      const { data: runningJobs } = await supabase
        .from('enrichment_jobs')
        .select('*')
        .eq('status', 'running')
        .order('created_at', { ascending: false })
        .limit(1);

      if (!runningJobs || runningJobs.length === 0) {
        // No running jobs, stop polling and reload data
        clearInterval(interval);
        setCurrentJob(null);
        await loadData();
      } else {
        setCurrentJob(runningJobs[0]);
      }
    }, 5000);

    // Clean up interval after 10 minutes
    setTimeout(() => clearInterval(interval), 600000);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-blue-500"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Running</Badge>;
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary"><Pause className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-[var(--header-height)] z-40 bg-card border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-xl font-heading font-semibold">Admin Tools</h1>
          <div className="flex items-center gap-2">
            <Button
              variant={location.pathname === '/admin/enrichment' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/enrichment')}
              size="sm"
            >
              Enrichment
            </Button>
            <Button
              variant={location.pathname === '/admin/data-cleanup' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/data-cleanup')}
              size="sm"
            >
              Data Cleanup
            </Button>
            <Button
              variant={location.pathname === '/admin/moderation' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/moderation')}
              size="sm"
            >
              Moderation
            </Button>
            <Button
              variant={location.pathname === '/admin/awards' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/awards')}
              size="sm"
            >
              Awards
            </Button>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Monitoring Dashboard */}
        <EnrichmentMonitoringDashboard />
        
        {/* Current Job Status */}
        {currentJob && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>Enrichment job is currently running...</span>
                {getStatusBadge(currentJob.status)}
              </div>
              {currentJob.total_books > 0 && (
                <div className="mt-2 space-y-1">
                  <Progress 
                    value={(currentJob.processed_books / currentJob.total_books) * 100} 
                    className="w-full"
                  />
                  <p className="text-sm text-muted-foreground">
                    {currentJob.processed_books} / {currentJob.total_books} books processed
                  </p>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* User Library Enrichment - Phase 1 Priority */}
        <UserLibraryEnrichmentPanel />

        {/* OpenLibrary Re-Enrichment - Phase 2 */}
        <OpenLibraryResetPanel />

        {/* Enrichment Queue Inspector */}
        <EnrichmentQueueTable />

        {/* Cover Cache Manager */}
        <CoverCacheManager />

        {/* Book Pre-Population Actions - Phase 2 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Book Pre-Population (Phase 2)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={testIsbndbSeries}
                disabled={isLoading}
                className="w-full"
                variant="outline"
              >
                <Database className="w-4 h-4 mr-2" />
                Test ISBNdb Series (ACOTAR)
              </Button>

              <Button
                onClick={cleanupEnrichmentQueue}
                disabled={isLoading}
                className="w-full"
                variant="destructive"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Cleanup Queue ({stats?.pending.toLocaleString() || 0})
              </Button>

              <Button
                onClick={populatePopularBooks}
                disabled={isLoading}
                className="w-full"
              >
                <Database className="w-4 h-4 mr-2" />
                Import 5,000 Popular Books
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={runWeeklyImport}
                disabled={isLoading}
                className="w-full"
                variant="secondary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Run Weekly Import
              </Button>
              
              <Button
                onClick={cleanupJunkBooks}
                disabled={isLoading}
                className="w-full"
                variant="destructive"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Delete Junk OpenLibrary Books
              </Button>
              
              <Button
                onClick={purgeNoCoverBooks}
                disabled={isLoading}
                className="w-full"
                variant="destructive"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Purge ALL No-Cover Books
              </Button>
            </div>
            
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Cleanup Queue:</strong> Removes books from enrichment queue that already have complete data or are too old to enrich.<br />
                <strong>Pre-population:</strong> Imports high-value books (popular, trending, award-winning) to ensure users always find what they're looking for.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Book className="w-4 h-4" />
                  Total Books
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total.toLocaleString()}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Enrichment Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Completed</span>
                    <span className="font-medium text-green-600">{stats.completed}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Pending</span>
                    <span className="font-medium text-yellow-600">{stats.pending}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Failed</span>
                    <span className="font-medium text-red-600">{stats.failed}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Duplicates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.duplicates.toLocaleString()}</div>
                <p className="text-sm text-muted-foreground">Marked as duplicates</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Cover Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>With Covers</span>
                    <span className="font-medium">{stats.with_covers}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Cached</span>
                    <span className="font-medium text-green-600">{stats.cached_covers}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Enrichment Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="hover:border-primary transition-colors cursor-pointer" onClick={() => !isLoading && !currentJob && startEnrichmentJob()}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Start Full Enrichment</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Run complete enrichment process: deduplication, Google Books API enrichment, and cover caching
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:border-primary transition-colors cursor-pointer" onClick={() => !isLoading && runDeduplication()}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Run Deduplication</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Find and merge duplicate book entries based on ISBN and title matching
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:border-primary transition-colors cursor-pointer" onClick={() => !isLoading && cacheCovers()}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Cache Covers</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Download and cache book covers to our storage for better reliability
                  </p>
                </CardContent>
              </Card>
              
              <Card className="hover:border-primary transition-colors cursor-pointer" onClick={() => !isLoading && fixAllForeignCoversHandler()}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className={`w-5 h-5 text-primary ${isLoading ? 'animate-spin' : ''}`} />
                    <span className="font-semibold">Fix All Foreign Covers</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Clear and re-enrich ALL books with foreign/unknown language covers
                  </p>
                  {fixProgress && (
                    <p className="text-xs text-primary mt-2">{fixProgress}</p>
                  )}
                </CardContent>
              </Card>
              
              <Card className="hover:border-primary transition-colors cursor-pointer" onClick={() => !isLoading && runQualityCheck()}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Run Quality Check</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Find books with potential identity or data quality issues
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Quality Check Results */}
        {showQualityCheck && qualitySummary && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Quality Check Results</CardTitle>
                <Badge variant="outline">
                  {qualityIssues.length} issues found
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-red-500/50 bg-red-500/5">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-red-600">{qualitySummary.high}</div>
                    <p className="text-sm text-muted-foreground">High Priority</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-500/50 bg-orange-500/5">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-orange-600">{qualitySummary.medium}</div>
                    <p className="text-sm text-muted-foreground">Medium Priority</p>
                  </CardContent>
                </Card>
                <Card className="border-yellow-500/50 bg-yellow-500/5">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-yellow-600">{qualitySummary.low}</div>
                    <p className="text-sm text-muted-foreground">Low Priority</p>
                  </CardContent>
                </Card>
              </div>

              {/* Issues List */}
              <div className="space-y-3">
                {qualityIssues.map((issue, idx) => (
                  <Card 
                    key={idx} 
                    className={
                      issue.severity === 'high' ? 'border-red-500/50' :
                      issue.severity === 'medium' ? 'border-orange-500/50' :
                      'border-yellow-500/50'
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={
                              issue.severity === 'high' ? 'destructive' :
                              issue.severity === 'medium' ? 'default' :
                              'secondary'
                            }>
                              {issue.severity}
                            </Badge>
                            <Badge variant="outline">{issue.type.replace('_', ' ')}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {issue.user_count} user{issue.user_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <h4 className="font-semibold truncate">{issue.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            by {issue.authors.join(', ')}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {issue.message}
                          </p>
                          {issue.confidence && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Confidence: {issue.confidence}%
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/book/${issue.book_id}`)}
                        >
                          View Book
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Series Data Management */}
        <SeriesManagementCard isLoading={isLoading} setIsLoading={setIsLoading} toast={toast} />

        {/* Recent Jobs */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Enrichment Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No enrichment jobs found</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{job.job_type.replace('_', ' ')}</span>
                        {getStatusBadge(job.status)}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(job.created_at).toLocaleString()}
                      </span>
                    </div>
                    
                    {job.total_books > 0 && (
                      <div className="mb-2">
                        <Progress 
                          value={job.total_books > 0 ? (job.processed_books / job.total_books) * 100 : 0}
                          className="w-full"
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          {job.processed_books} / {job.total_books} books processed
                          {job.failed_books > 0 && ` (${job.failed_books} failed)`}
                        </p>
                      </div>
                    )}
                    
                    {job.error_message && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertDescription>{job.error_message}</AlertDescription>
                      </Alert>
                    )}
                    
                    {job.results && job.results.message && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {job.results.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// Series Management Card Component
function SeriesManagementCard({ 
  isLoading, 
  setIsLoading, 
  toast 
}: { 
  isLoading: boolean; 
  setIsLoading: (v: boolean) => void; 
  toast: any;
}) {
  const [seriesStats, setSeriesStats] = useState<{
    totalWithSeries: number;
    uniqueSeriesNames: number;
    booksWithPosition: number;
    booksWithTotal: number;
  } | null>(null);
  const [lastResult, setLastResult] = useState<string>('');

  useEffect(() => {
    loadSeriesStats();
  }, []);

  const loadSeriesStats = async () => {
    const stats = await getSeriesStats();
    setSeriesStats(stats);
  };

  const handleCleanupJunk = async () => {
    setIsLoading(true);
    try {
      const result = await cleanupJunkSeriesData();
      setLastResult(`Cleaned ${result.cleaned} junk series entries`);
      toast({ title: "Cleanup Complete", description: `Removed ${result.cleaned} false-positive series names` });
      loadSeriesStats();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to cleanup junk series data" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTitleBackfill = async () => {
    setIsLoading(true);
    try {
      const result = await backfillSeriesFromTitles(1000);
      setLastResult(`Processed ${result.processed} books, updated ${result.updated} with series data`);
      toast({ title: "Backfill Complete", description: `Found series data for ${result.updated} books` });
      loadSeriesStats();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to backfill series from titles" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteBackfill = async () => {
    setIsLoading(true);
    try {
      const result = await runCompleteSeriesBackfill();
      setLastResult(`Updated ${result.booksUpdated} books, tracked ${result.seriesTracked} series, cleaned ${result.junkCleaned} junk entries`);
      toast({ title: "Complete Backfill Done", description: `Enriched ${result.booksUpdated} books with series data` });
      loadSeriesStats();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to run complete series backfill" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Book className="w-5 h-5" />
          Series Data Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        {seriesStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-muted rounded-lg p-3">
              <div className="text-2xl font-bold">{seriesStats.totalWithSeries.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground">Books with Series</p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="text-2xl font-bold">{seriesStats.uniqueSeriesNames.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground">Unique Series</p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="text-2xl font-bold">{seriesStats.booksWithPosition.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground">With Position</p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="text-2xl font-bold">{seriesStats.booksWithTotal.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground">With Total Count</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={handleCleanupJunk}
            disabled={isLoading}
            variant="outline"
            className="w-full"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Cleanup Junk Series
          </Button>
          
          <Button
            onClick={handleTitleBackfill}
            disabled={isLoading}
            variant="outline"
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Backfill from Titles
          </Button>
          
          <Button
            onClick={handleCompleteBackfill}
            disabled={isLoading}
            className="w-full"
          >
            <Play className="w-4 h-4 mr-2" />
            Run Complete Backfill
          </Button>
        </div>

        {lastResult && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{lastResult}</AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Cleanup Junk:</strong> Removes false-positive series names (editions, languages, etc.)<br />
            <strong>Backfill from Titles:</strong> Detects series from title patterns like "(Series #1)"<br />
            <strong>Complete Backfill:</strong> Runs all detection methods including 100+ known series patterns
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}